/**
 * CartItem — Single line item in the cart.
 * Used in both the CartDrawer and the Cart page.
 */

import { Link } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useCart } from "@/features/cart/store/cart-store";
import { QuantitySelector } from "@/components/forms/quantity-selector";
import { formatPrice } from "@/components/forms/price-display";
import { type Product, getProductSlug } from "@/lib/products";

type CartItemProps = {
  product: Product;
  qty: number;
  /** Show expanded layout (for cart page vs drawer) */
  expanded?: boolean;
};

export function CartItem({ product, qty, expanded = false }: CartItemProps) {
  const { setQty, remove } = useCart();
  const slug = getProductSlug(product);

  if (expanded) {
    return (
      <li className="flex gap-6 py-6 sm:py-8">
        <Link to="/product/$id" params={{ id: slug }} className="shrink-0">
          <img
            src={product.image}
            alt={product.name}
            width={120}
            height={150}
            className="h-32 w-28 object-cover bg-stone"
          />
        </Link>
        <div className="flex flex-1 flex-col justify-between">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="eyebrow">{product.category}</p>
              <Link to="/product/$id" params={{ id: slug }}>
                <h3 className="mt-1 text-lg font-medium transition-colors hover:text-primary">
                  {product.name}
                </h3>
              </Link>
              <p className="mt-1 text-xs text-muted-foreground">{product.note}</p>
            </div>
            <p className="font-display text-lg tabular-nums">{formatPrice(product.price * qty)}</p>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <QuantitySelector value={qty} onChange={(v) => setQty(product.id, v)} size="sm" />
            <button
              onClick={() => remove(product.id)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-destructive"
              aria-label={`Remove ${product.name} from bag`}
            >
              <Trash2 className="h-4 w-4" />
              <span>Remove</span>
            </button>
          </div>
        </div>
      </li>
    );
  }

  // Compact layout for drawer
  return (
    <li className="flex gap-4 py-4">
      <Link to="/product/$id" params={{ id: slug }} className="shrink-0">
        <img
          src={product.image}
          alt={product.name}
          width={64}
          height={80}
          className="h-20 w-16 object-cover bg-stone"
        />
      </Link>
      <div className="flex flex-1 flex-col justify-between min-w-0">
        <div>
          <Link to="/product/$id" params={{ id: slug }} className="block">
            <p className="text-xs font-semibold truncate hover:text-primary transition-colors">
              {product.name}
            </p>
            <p className="text-[11px] text-muted-foreground line-clamp-1">{product.note}</p>
          </Link>
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            <QuantitySelector value={qty} onChange={(v) => setQty(product.id, v)} size="sm" />
            <button
              onClick={() => remove(product.id)}
              className="p-1 text-stone-400 hover:text-rose-600 transition-colors rounded hover:bg-stone-100"
              aria-label={`Remove ${product.name} from bag`}
              title="Remove item"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <span className="text-xs tabular-nums font-bold text-stone-900">
            {formatPrice(product.price * qty)}
          </span>
        </div>
      </div>
    </li>
  );
}
